import type { FleetMissionOverviewDateRange } from "./FleetMissionStorageClient";

export type MissionOverviewRangeSelection = "allTime" | "thisMonth" | "thisWeek" | "custom";

export type MissionOverviewRangeSettings = {
    selection: MissionOverviewRangeSelection;
    customStartDate: string;
    customEndDate: string;
};

export const MISSION_OVERVIEW_RANGE_STORAGE_KEY = "fleetMissionOverviewDateRange";

const selections: MissionOverviewRangeSelection[] = ["allTime", "thisMonth", "thisWeek", "custom"];
const isoDatePattern = /^\d{4}-\d{2}-\d{2}$/;

const isIsoCalendarDate = (value: string): boolean => {
    if (!isoDatePattern.test(value)) return false;
    const parsed = new Date(`${value}T00:00:00Z`);
    return !Number.isNaN(parsed.getTime()) && parsed.toISOString().slice(0, 10) === value;
};

export const toIsoCalendarDate = (value: Date): string => {
    const year = value.getFullYear();
    const month = String(value.getMonth() + 1).padStart(2, "0");
    const day = String(value.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
};

const toLocalCalendarMidnight = (value: string): Date => {
    const [year, month, day] = value.split("-").map(Number);
    return new Date(year, month - 1, day, 0, 0, 0, 0);
};

export const toUtcMissionOverviewDateRange = (
    range: FleetMissionOverviewDateRange,
): { startAt: string; endAt: string } => {
    const localEndExclusive = toLocalCalendarMidnight(range.endDate);
    // Advance the local calendar date before converting to UTC. Adding a
    // fixed 24 hours here would be wrong across daylight-saving transitions.
    localEndExclusive.setDate(localEndExclusive.getDate() + 1);
    return {
        startAt: toLocalCalendarMidnight(range.startDate).toISOString(),
        endAt: localEndExclusive.toISOString(),
    };
};

export const defaultMissionOverviewRangeSettings = (
    now = new Date()
): MissionOverviewRangeSettings => ({
    selection: "thisMonth",
    customStartDate: toIsoCalendarDate(new Date(now.getFullYear(), now.getMonth(), 1)),
    customEndDate: toIsoCalendarDate(now),
});

export const loadMissionOverviewRangeSettings = (
    storage?: Pick<Storage, "getItem"> | null,
    now = new Date()
): MissionOverviewRangeSettings => {
    const fallback = defaultMissionOverviewRangeSettings(now);
    try {
        const target =
            storage === undefined
                ? typeof window === "undefined"
                    ? null
                    : window.localStorage
                : storage;
        if (!target) return fallback;
        const raw = target.getItem(MISSION_OVERVIEW_RANGE_STORAGE_KEY);
        if (!raw) return fallback;
        const saved = JSON.parse(raw) as Partial<MissionOverviewRangeSettings>;
        return {
            selection: selections.includes(saved.selection as MissionOverviewRangeSelection)
                ? (saved.selection as MissionOverviewRangeSelection)
                : fallback.selection,
            customStartDate: isIsoCalendarDate(saved.customStartDate ?? "")
                ? (saved.customStartDate as string)
                : fallback.customStartDate,
            customEndDate: isIsoCalendarDate(saved.customEndDate ?? "")
                ? (saved.customEndDate as string)
                : fallback.customEndDate,
        };
    } catch {
        return fallback;
    }
};

export const saveMissionOverviewRangeSettings = (
    settings: MissionOverviewRangeSettings,
    storage?: Pick<Storage, "setItem"> | null
): void => {
    try {
        const target =
            storage === undefined
                ? typeof window === "undefined"
                    ? null
                    : window.localStorage
                : storage;
        if (!target) return;
        target.setItem(MISSION_OVERVIEW_RANGE_STORAGE_KEY, JSON.stringify(settings));
    } catch {
        // Browsing can continue when storage is disabled or full.
    }
};

export const resolveMissionOverviewDateRange = (
    settings: MissionOverviewRangeSettings,
    now = new Date()
): FleetMissionOverviewDateRange | null => {
    const endDate = toIsoCalendarDate(now);
    if (settings.selection === "allTime") return null;
    if (settings.selection === "thisMonth") {
        return {
            startDate: toIsoCalendarDate(new Date(now.getFullYear(), now.getMonth(), 1)),
            endDate,
        };
    }
    if (settings.selection === "thisWeek") {
        const monday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        monday.setDate(monday.getDate() - ((monday.getDay() + 6) % 7));
        return { startDate: toIsoCalendarDate(monday), endDate };
    }
    if (
        !isIsoCalendarDate(settings.customStartDate) ||
        !isIsoCalendarDate(settings.customEndDate) ||
        settings.customStartDate > settings.customEndDate
    ) {
        return null;
    }
    return { startDate: settings.customStartDate, endDate: settings.customEndDate };
};
